import { useEffect, useRef, useCallback } from 'react';

import { Screen } from '@/types';

type NavState =
  | Screen
  | 'lightbox'
  | 'settings'
  | 'seeAll'
  | 'profile'
  | 'credits'
  | 'search';

interface UseBrowserHistoryOptions {
  screen: Screen;
  onBackToLibrary: () => void;
  onBackFromFiles: () => void;
  onBackFromLightbox: () => void;
  onBackFromSettings?: () => void;
  onBackFromProfile?: () => void;
  onBackFromCredits?: () => void;
  onBackFromSearch?: () => void;
  onBackFromSeeAll: () => void;
  onCloseApp: () => void;
}

// Module-level ref so any code can push nav state via `pushBrowserNav`
let pushNavFn: ((next: NavState) => void) | null = null;

/**
 * Push a navigation state to the browser history stack.
 * Call this when opening overlays (lightbox, settings).
 * Screen transitions are handled automatically by useBrowserHistory.
 */
export const pushBrowserNav = (
  next: 'lightbox' | 'settings' | 'seeAll' | 'profile' | 'credits' | 'search'
) => {
  pushNavFn?.(next);
};

export const useBrowserHistory = ({
  screen,
  onBackToLibrary,
  onBackFromFiles,
  onBackFromLightbox,
  onBackFromSettings,
  onBackFromProfile,
  onBackFromCredits,
  onBackFromSearch,
  onBackFromSeeAll,
  onCloseApp,
}: UseBrowserHistoryOptions) => {
  const currentNavRef = useRef<NavState>(screen);
  const isPopstateRef = useRef(false);
  const prevScreenRef = useRef<Screen>(screen);

  const callbacksRef = useRef({
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onBackFromProfile,
    onBackFromCredits,
    onBackFromSearch,
    onBackFromSeeAll,
    onCloseApp,
  });
  useEffect(() => {
    callbacksRef.current = {
      onBackToLibrary,
      onBackFromFiles,
      onBackFromLightbox,
      onBackFromSettings,
      onBackFromProfile,
      onBackFromCredits,
      onBackFromSearch,
      onBackFromSeeAll,
      onCloseApp,
    };
  }, [
    onBackToLibrary,
    onBackFromFiles,
    onBackFromLightbox,
    onBackFromSettings,
    onBackFromProfile,
    onBackFromCredits,
    onBackFromSearch,
    onBackFromSeeAll,
    onCloseApp,
  ]);

  const pushNav = useCallback((next: NavState) => {
    currentNavRef.current = next;
    window.history.pushState({ appNav: next }, '');
  }, []);

  // Set initial history state and register module-level pushNav
  useEffect(() => {
    window.history.replaceState({ appNav: screen }, '');
    pushNavFn = pushNav;
    return () => {
      pushNavFn = null;
    };
    // Only run on mount — screen captured via ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushNav]);

  // Auto-push history when screen changes (covers all setScreen calls across hooks)
  useEffect(() => {
    const from = prevScreenRef.current;
    if (from !== screen) {
      prevScreenRef.current = screen;

      // Skip push when the screen change was triggered by popstate (back button).
      // The popstate handler already set currentNavRef to the correct nav level
      // (read from the history stack), so don't overwrite it here.
      if (isPopstateRef.current) {
        isPopstateRef.current = false;
        return;
      }

      // Create is a substate of Library, not a screen of its own (see MobileLayout),
      // so arriving at Files from it counts as Library -> Files: no second entry.
      if (from === Screen.Create && screen === Screen.Files) return;

      pushNav(screen);
    }
  }, [screen, pushNav]);

  // Listen for popstate (Android back button / browser back)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const state = e.state as { appNav?: NavState } | null;
      const incoming = state?.appNav ?? Screen.Library;
      const { current } = currentNavRef;

      if (current === 'lightbox') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromLightbox();
        // No screen change — don't set isPopstateRef
      } else if (current === 'settings') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromSettings?.();
        // No screen change — don't set isPopstateRef
      } else if (current === 'profile') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromProfile?.();
        // No screen change — don't set isPopstateRef
      } else if (current === 'credits') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromCredits?.();
        // No screen change — don't set isPopstateRef
      } else if (current === 'search') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromSearch?.();
        // No screen change — don't set isPopstateRef
      } else if (current === 'seeAll') {
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromSeeAll();
        // No screen change — don't set isPopstateRef
      } else if (current === Screen.Files) {
        isPopstateRef.current = true;
        currentNavRef.current = incoming;
        callbacksRef.current.onBackFromFiles();
      } else if (current === Screen.Create) {
        isPopstateRef.current = true;
        // Route to whatever the history stack says is underneath Create — the
        // Library root, or a still-open 'seeAll' page. The stack is the source
        // of truth, so this generic branch needs no feature/experiment knowledge.
        currentNavRef.current = incoming;
        callbacksRef.current.onBackToLibrary();
      } else if (current === Screen.Library) {
        // Already at root — restore state and close app
        window.history.pushState({ appNav: Screen.Library }, '');
        callbacksRef.current.onCloseApp();
      } else {
        // loading / result or other transient screens — just sync ref
        currentNavRef.current = incoming;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { pushNav };
};
